import { inject, Injectable } from '@angular/core';
import { Column } from '@orion-platform/datagrid';
import { map, Observable } from 'rxjs';
import { UserSettings } from '../models/supply-visibility.model';
import { ColumnsState, DefaultColumnsSource } from '../types/columns.types';
import { loadDefaultColumns } from '../utils/columns.utils';
import { toStoredColumns } from '../utils/general.utils';
import { SupplyVisibilityService } from './supply-visibility.service';

/** What a page has to say about its columns; the rest is the same everywhere. */
export type ModuleColumnsSource = Omit<DefaultColumnsSource, 'persistDefaults' | 'userSettings'>;

/**
 * Resolves a page's columns.
 *
 * Three questions differ between pages — what the schema is, which key it is
 * saved under, and which form fields the plant allows — and one answer never
 * does: the settings are read from, and written to, the one endpoint that
 * holds them. That part lives here, so a module says only what is its own.
 */
@Injectable({
  providedIn: 'root',
})
export class ColumnsService {
  private readonly settings = inject(SupplyVisibilityService);

  /**
   * @param {ModuleColumnsSource} source What this module's columns are.
   * @returns {Observable<ColumnsState>} The columns, and whether they are still the defaults.
   */
  load(source: ModuleColumnsSource): Observable<ColumnsState> {
    return loadDefaultColumns({
      ...source,
      persistDefaults: (key: string, columns: Column[]): Observable<void> =>
        this.settings
          // The same trimmed shape a save writes, so the first record a user
          // gets looks like every one that follows it.
          .addUserSettings({ data: JSON.stringify(toStoredColumns(columns)), key }, false)
          .pipe(map((): void => undefined)),
      userSettings: (key: string): Observable<UserSettings> => this.settings.getUserSettings(key),
    });
  }
}
